import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, onSnapshot, query, orderBy } from 'firebase/firestore';

export interface UnverifiedUser {
  uid: string;
  email: string | null;
  created: string;
  lastSignIn: string | null;
  displayName: string | null;
  phoneNumber: string | null;
  flaggedAt: any; // Firestore timestamp
}

export class UnverifiedUsersService {
  private static collectionName = 'unverifiedUsers';

  /**
   * Get all unverified users
   */
  static async getUnverifiedUsers(): Promise<UnverifiedUser[]> {
    try {
      const querySnapshot = await getDocs(collection(db, this.collectionName));
      return querySnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      } as UnverifiedUser));
    } catch (error) {
      console.error('Error fetching unverified users:', error);
      throw error;
    }
  }

  /**
   * Get a specific unverified user by UID
   */
  static async getUnverifiedUser(uid: string): Promise<UnverifiedUser | null> {
    try {
      const docRef = doc(db, this.collectionName, uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          uid: docSnap.id,
          ...docSnap.data()
        } as UnverifiedUser;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching unverified user:', error);
      throw error;
    }
  }

  /**
   * Listen to unverified users changes
   */
  static listenToUnverifiedUsers(callback: (users: UnverifiedUser[]) => void) {
    const q = query(collection(db, this.collectionName), orderBy('flaggedAt', 'desc'));
    
    return onSnapshot(q, (querySnapshot) => {
      const users = querySnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      } as UnverifiedUser));
      
      callback(users);
    }, (error) => {
      console.error('Error listening to unverified users:', error);
    });
  }

  /**
   * Check if a user is unverified
   */
  static async isUserUnverified(uid: string): Promise<boolean> {
    try {
      const user = await this.getUnverifiedUser(uid);
      return user !== null;
    } catch (error) {
      console.error('Error checking if user is unverified:', error);
      return false;
    }
  }
}

